import { useState, useRef, useEffect, useCallback } from 'react'

// Drives a proxy scrollbar sitting above a horizontally scrolling element.
//
// A scrollbar cannot be moved to the top of its own container, so the rail is a
// separate element that scrolls an empty spacer as wide as the real content.
// Scrolling either one mirrors the other; the guard stops that mirroring from
// bouncing back and fighting the gesture.
const useScrollRail = (scrollRef, active) => {
  const railRef = useRef(null)
  const syncingRef = useRef(false)
  const [ contentWidth, setContentWidth ] = useState(0)

  // The rail is inset from the wrapper, so the spacer is shortened by the same
  // amount — otherwise the two have different scroll ranges and the thumb both
  // mis-sizes and runs past the end of the content.
  const measure = useCallback(() => {
    const content = scrollRef.current
    const rail = railRef.current
    if (!content) {
      return
    }

    const inset = rail ? content.clientWidth - rail.clientWidth : 0
    setContentWidth(Math.max(0, content.scrollWidth - inset))
  }, [ scrollRef ])

  useEffect(() => {
    const content = scrollRef.current
    const rail = railRef.current
    if (!active || !content || !rail) {
      return undefined
    }

    measure()

    const mirror = (from, to) => () => {
      if (syncingRef.current) {
        return
      }

      syncingRef.current = true
      to.scrollLeft = from.scrollLeft
      // Released on the next frame: assigning scrollLeft fires the other
      // element's scroll event asynchronously.
      requestAnimationFrame(() => { syncingRef.current = false })
    }

    const onContentScroll = mirror(content, rail)
    const onRailScroll = mirror(rail, content)

    content.addEventListener('scroll', onContentScroll, { passive: true })
    rail.addEventListener('scroll', onRailScroll, { passive: true })

    // Observe the tables, not just the wrapper: columns arriving later grow
    // scrollWidth without changing the wrapper's own size, which would leave
    // the spacer short and the thumb too long.
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
    if (observer) {
      observer.observe(content)
      Array.from(content.children).forEach((child) => observer.observe(child))
    }
    window.addEventListener('resize', measure)

    return () => {
      content.removeEventListener('scroll', onContentScroll)
      rail.removeEventListener('scroll', onRailScroll)
      if (observer) {
        observer.disconnect()
      }
      window.removeEventListener('resize', measure)
    }
  }, [ active, scrollRef, measure ])

  return { railRef, contentWidth }
}

export default useScrollRail
